/* eslint-disable @harmony/stencil/no-import-css */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';
import { Tile, TileText, Typography } from '@one-thd/sui-atomic-components';
import { GIFTCARD_RELOAD_AMOUNTS, VERBIAGE } from '../../utils/consts';
import './AmountOptions.scss';

const AmountOptions = ({ minAmount, maxAmount, amount, onAmountChange }) => {
  const [customFieldFocus, setCustomFieldFocus] = useState(false);
  const [errorText, setErrorText] = useState('');
  const inputRef = useRef(null);

  const handleSetError = (errorMsg) => {
    if (errorMsg) {
      setErrorText(errorMsg);
      onAmountChange(0);
    } else {
      setErrorText('');
    }
  };

  const handleChangeCustomAmount = (input) => {
    if (input) {
      const val = parseFloat(input) >= 0 ? parseFloat(input) : 0;
      onAmountChange(val);
      handleSetError('');
    } else {
      handleSetError(VERBIAGE.enterAmount);
    }
  };

  const validateCustomAmount = (input) => {
    if (input) {
      let custAmount = parseFloat(input?.target?.value);
      if (!custAmount) {
        handleSetError(VERBIAGE.enterAmount);
      }
      if (custAmount > maxAmount || (custAmount < minAmount && custAmount > 0)) {
        handleSetError(`Please enter an amount between $${minAmount} and $${maxAmount}`);
      }
    } else {
      handleSetError(VERBIAGE.enterAmount);
    }
  };

  const focusCustomAmount = (input) => {
    if (input) {
      const customAmountValue = parseFloat(input?.currentTarget?.value);
      setCustomFieldFocus(true);
      onAmountChange(Number.isNaN(customAmountValue) ? 0 : customAmountValue);
    } else {
      handleSetError(VERBIAGE.enterAmount);
    }
  };

  const onClickAmountButton = (amountOption) => {
    onAmountChange(amountOption);
    handleSetError('');
    setCustomFieldFocus(false);
  };

  const amountButtons = GIFTCARD_RELOAD_AMOUNTS.map((amountOption) => (
    <Tile
      key={amountOption}
      value={amountOption}
      aria-label={amountOption}
      selected={amountOption === amount && !customFieldFocus}
      disabled={maxAmount <= amountOption}
      unavailable={maxAmount <= amountOption}
      onClick={() => {
        onClickAmountButton(amountOption);
        if (inputRef && inputRef.current) {
          inputRef.current.value = '';
        }
      }}
    >
      <TileText>${amountOption}</TileText>
    </Tile>
  ));

  const amountButtonCustom = (
    <div className="sui-custom_amount_input">
      <Tile value="custom" selected={customFieldFocus}>
        <Typography component="span">$</Typography>
        <IMaskInput
          mask="[0000.00]"
          aria-describedby="custom-amount-input"
          onAccept={handleChangeCustomAmount}
          onFocus={focusCustomAmount}
          onBlur={validateCustomAmount}
          inputRef={inputRef}
          placeholder="Custom"
          type="number"
        />
      </Tile>
    </div>
  );

  const maximumTile = (
    <Tile
      value={maxAmount}
      selected={maxAmount === amount && !customFieldFocus}
      onClick={() => {
        onClickAmountButton(maxAmount);
        if (inputRef && inputRef.current) {
          inputRef.current.value = '';
        }
      }}
    >
      <TileText>Maximum ${maxAmount.toFixed(2)}</TileText>
    </Tile>
  );
  return (
    <div className="sui-flex sui-flex-wrap sui-gap-3 sui-my-4">
      {amountButtons}
      {amountButtonCustom}
      {maximumTile}
      {errorText && <span className="sui-text-xs sui-text-danger">{errorText}</span>}
    </div>
  );
};

AmountOptions.propTypes = {
  amount: PropTypes.number,
  minAmount: PropTypes.number,
  maxAmount: PropTypes.number,
  onAmountChange: PropTypes.func
};

AmountOptions.defaultProps = {
  minAmount: 1.0,
  maxAmount: 2000.0,
  amount: 0,
  onAmountChange: () => {}
};

export default AmountOptions;
